import { isProcessing } from "@/shared/api/knowledge";
import { fileType } from "@/shared/components/icons/filetype";
import { extOf } from "./format";
import type { KbTree } from "./hooks/useTree";

import type { KbNode, KbSearchHit, PersonRef } from "@/shared/types";

/* What the table lists and in what order: a file's status and type, the filters, the sort,
   and a search's rows (names anywhere in the base, then files whose text matched). */

export type FileStatus = "indexed" | "processing" | "not-indexed" | "failed";

export const STATUS_LABEL: Record<FileStatus, string> = {
  indexed: "Indexed",
  processing: "Processing",
  "not-indexed": "Not indexed",
  failed: "Couldn't read",
};

/** Where a file stands with the agents that read it; null for a folder. */
export function statusOf(node: KbNode): FileStatus | null {
  const file = node.file;
  if (!file) return null;
  if (file.ingest_status === "failed") return "failed";
  if (file.ingest_status !== "extracted" || isProcessing(node)) return "processing";
  return file.tokens == null ? "not-indexed" : "indexed";
}

export const FOLDER_TYPE = "Folder";
/** "Folder", or the file's family as its icon names it: "PDF", "Spreadsheet", "Image". */
export const typeOf = (node: KbNode) => (node.kind === "folder" ? FOLDER_TYPE : fileType(extOf(node.name) || "file").label);

export const NOBODY = "nobody";
export const ownerOf = (node: KbNode) => node.created_by?.id ?? NOBODY;

export interface Row {
  node: KbNode;
  /** Search only: the folders it sits in, or the base's name at the top level. */
  where?: string;
  /** Search only: the matched text, `<mark>`ed by the server. */
  snippet?: string;
}

export interface Filters {
  types: string[];
  statuses: FileStatus[];
  owners: string[];
}
export const NO_FILTERS: Filters = { types: [], statuses: [], owners: [] };
export const filterCount = (f: Filters) => f.types.length + f.statuses.length + f.owners.length;

export type SortKey = "name" | "modified" | "size" | "tokens";
export interface Sort {
  key: SortKey;
  descending: boolean;
}
export const DEFAULT_SORT: Sort = { key: "name", descending: false };
/** The order a column sorts in when it is first picked: names A to Z, the rest biggest first. */
export const FIRST_DESCENDING: Record<SortKey, boolean> = { name: false, modified: true, size: true, tokens: true };

function passes(node: KbNode, filters: Filters): boolean {
  const status = statusOf(node);
  if (filters.types.length && !filters.types.includes(typeOf(node))) return false;
  if (filters.statuses.length && !(status && filters.statuses.includes(status))) return false;
  return !filters.owners.length || filters.owners.includes(ownerOf(node));
}

const byName = (a: KbNode, b: KbNode) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });

function measure(node: KbNode, key: SortKey, tree: KbTree): number {
  if (key === "modified") return Date.parse(node.updated_at) || 0;
  const sums = node.file ? { bytes: node.file.bytes, tokens: node.file.tokens ?? 0 } : tree.totals(node.id);
  return key === "size" ? sums.bytes : sums.tokens;
}

/** Filtered, then sorted with folders first; ties go by name. */
export function view(rows: Row[], filters: Filters, sort: Sort, tree: KbTree): Row[] {
  const sign = sort.descending ? -1 : 1;
  const compare = (a: Row, b: Row) => {
    if (a.node.kind !== b.node.kind) return a.node.kind === "folder" ? -1 : 1;
    const order = sort.key === "name" ? byName(a.node, b.node) : measure(a.node, sort.key, tree) - measure(b.node, sort.key, tree);
    return order * sign || byName(a.node, b.node);
  };
  return rows.filter((r) => passes(r.node, filters)).sort(compare);
}

/** Names that contain the query anywhere in the base, then the files whose text matched
    (the server's full-text hits), each once. */
export function searchRows(tree: KbTree, query: string, hits: KbSearchHit[], baseName: string): Row[] {
  const q = query.trim().toLowerCase();
  const where = (node: KbNode) => tree.pathTo(node.parent_id).map((f) => f.name).join(" / ") || baseName;
  const snippets = new Map(hits.map((h) => [h.node_id, h.snippet]));
  const rows: Row[] = [];
  const seen = new Set<string>();
  const add = (node: KbNode | undefined) => {
    if (!node || seen.has(node.id)) return;
    seen.add(node.id);
    rows.push({ node, where: where(node), snippet: snippets.get(node.id) });
  };
  if (q) tree.nodes.filter((n) => n.name.toLowerCase().includes(q)).forEach(add);
  hits.forEach((h) => add(tree.byId(h.node_id)));
  return rows;
}

export interface Facet<T extends string = string> {
  value: T;
  count: number;
}

/** The values each filter can take among these rows, with how many rows have each. A value
    already picked stays offered at 0, so it can be taken off where nothing has it. */
export function facets(rows: Row[], picked: Filters) {
  const tally = <T extends string>(values: (T | null)[], keep: T[]): Facet<T>[] => {
    const counts = new Map<T, number>(keep.map((v) => [v, 0]));
    for (const v of values) if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
    return [...counts].map(([value, count]) => ({ value, count }));
  };
  const nodes = rows.map((r) => r.node);
  return {
    types: tally(nodes.map(typeOf), picked.types).sort((a, b) => b.count - a.count || a.value.localeCompare(b.value)),
    statuses: tally(nodes.map(statusOf), picked.statuses).sort(
      (a, b) => Object.keys(STATUS_LABEL).indexOf(a.value) - Object.keys(STATUS_LABEL).indexOf(b.value),
    ),
    owners: tally(nodes.map(ownerOf), picked.owners),
  };
}

/** Who each owner key is, across the whole base: a filter kept from another folder still
    names its person. */
export const peopleIn = (nodes: KbNode[]) => new Map<string, PersonRef | null>(nodes.map((n) => [ownerOf(n), n.created_by]));
